import { ApolloClient, ApolloProvider, InMemoryCache, createHttpLink, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient as createWSClient } from 'graphql-ws';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';
import { useState, useEffect, useRef, type PropsWithChildren } from 'react';
import { useInitErrorHandling } from 'utils/errorHandling/errorHandling.ts';

export function NhostApolloProvider({ children }: PropsWithChildren) {
  const nhost = useNhostClient();
  const [client, setClient] = useState<ApolloClient<any> | null>(null);

  const { apolloErrorLink } = useInitErrorHandling();
  // keep a ref to the error link so we don't force recreation on every render
  const apolloErrorLinkRef = useRef(apolloErrorLink);
  useEffect(() => {
    apolloErrorLinkRef.current = apolloErrorLink;
  }, [apolloErrorLink]);

  useEffect(() => {
    const httpLink = createHttpLink({ uri: nhost.graphql.url });

    const authLink = setContext((_, { headers }) => {
      const session = nhost.getUserSession();
      return {
        headers: {
          ...headers,
          ...(session?.accessToken && { authorization: `Bearer ${session.accessToken}` }),
        },
      };
    });

    // build the HTTP link chain (auth -> http)
    const terminatingHttpLink = authLink.concat(httpLink);

    // put error link first if present to ensure it can intercept
    const errorAwareHttpLink = apolloErrorLinkRef.current
      ? apolloErrorLinkRef.current.concat(terminatingHttpLink)
      : terminatingHttpLink;

    let link: any = errorAwareHttpLink;

    // Only try to create a WS link in the browser environment
    if (typeof window !== 'undefined') {
      try {
        const wsUrl = nhost.graphql.url.replace(/^http/, 'ws');
        const wsClient = createWSClient({
          url: wsUrl,
          connectionParams: async () => {
            const session = nhost.getUserSession();
            return {
              headers: {
                ...(session?.accessToken && { authorization: `Bearer ${session.accessToken}` }),
              },
            };
          },
          // optional: lazy, retry, etc.
        });

        const wsLink = new GraphQLWsLink(wsClient);

        // route subscription operations to websocket, others to HTTP (errorAwareHttpLink)
        link = split(
          ({ query }) => {
            const definition = getMainDefinition(query);
            return (
              definition.kind === 'OperationDefinition' &&
              (definition as any).operation === 'subscription'
            );
          },
          wsLink,
          errorAwareHttpLink
        );
      } catch {
        // fallback to HTTP-only link if ws creation fails
      }
    }

    const apolloClient = new ApolloClient({ link, cache: new InMemoryCache() });
    setClient(apolloClient);
  }, [nhost]);

  if (!client) return null;

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
